import { getTableById } from '@pkg/db/utils'
import type { Kysely, Transaction } from 'kysely'
import { type HLCString, parseHLC } from './hlc'
import type { SyncMessage } from './types'

export interface ApplyMessageConfig<DB> {
  db: Kysely<DB>
  // Config can be extended here if needed
}

export function createApplyMessage<DB>(config: ApplyMessageConfig<DB>) {
  const { db } = config
  return async (messages: SyncMessage | SyncMessage[], trxFromParams?: Transaction<DB>) => {
    const execute = async (trx: Transaction<DB>) => {
      const messagesArray = Array.isArray(messages) ? messages : [messages]

      // Group messages by row to perform bulk updates per entity
      const rowUpdates = new Map<
        string,
        {
          values: Record<string, never>
          first: SyncMessage
          last: SyncMessage
          deletedAt?: string | null
          deletedBy?: string | null
        }
      >()

      for (const msg of messagesArray) {
        if (!rowUpdates.has(msg.row)) {
          rowUpdates.set(msg.row, { values: { id: msg.row }, first: msg, last: msg })
        }
        // biome-ignore lint/style/noNonNullAssertion: Has to be there. Set before
        const rowData = rowUpdates.get(msg.row)!

        // Update first/last message based on intentTs
        if (msg.intentTs < rowData.first.intentTs) {
          rowData.first = msg
        }
        if (msg.intentTs > rowData.last.intentTs) {
          rowData.last = msg
        }

        if (msg.column === 'deletedAt') {
          if (msg.value !== null) {
            // remove: derive deletedAt from intentTs, deletedBy from userId
            rowData.deletedAt = new Date(parseHLC(msg.intentTs as HLCString).time).toISOString()
            rowData.deletedBy = msg.userId
          } else {
            // restore: clear both fields
            rowData.deletedAt = null
            rowData.deletedBy = null
          }
        } else {
          rowData.values[msg.column as never] = msg.value as never
        }
      }

      // Execute updates per row
      for (const [row, rowData] of rowUpdates.entries()) {
        const table = getTableById(row as never)
        const { values, first, last, deletedAt, deletedBy } = rowData
        const { id, ...updateValuesWithoutAudit } = values

        const firstTime = new Date(parseHLC(first.intentTs as HLCString).time).toISOString()
        const lastTime = new Date(parseHLC(last.intentTs as HLCString).time).toISOString()

        const deleteFields = Object.hasOwn(rowData, 'deletedAt') ? { deletedAt, deletedBy } : {}

        const fullValues = {
          ...values,
          createdAt: firstTime,
          createdBy: first.userId,
          updatedAt: lastTime,
          updatedBy: last.userId,
          ...deleteFields,
        }

        const updateValues = {
          ...updateValuesWithoutAudit,
          updatedAt: lastTime,
          updatedBy: last.userId,
          ...deleteFields,
        }

        await trx
          .insertInto(table as never)
          .values(fullValues as never)
          .onConflict(oc => oc.column('id' as never).doUpdateSet(updateValues as never))
          .execute()
      }
    }
    return await (trxFromParams ? execute(trxFromParams) : db.transaction().execute(execute))
  }
}
